import { useApolloClient } from '@app/context/ApolloClientContext';
import { useUser } from '@app/context/UserContext';
import useChat from '@app/hooks/useChat';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Avatar, AvatarFallback, AvatarImage } from '@components/shadcn/avatar';
import { Badge } from '@components/shadcn/badge';
import { Button } from '@components/shadcn/button';
import { Markdown } from '@components/ui';
import {
  ChatBubble,
  ChatBubbleAction,
  ChatBubbleActionWrapper,
  ChatBubbleAvatar,
  ChatBubbleMessage,
} from '@components/ui/chat/chat-bubble';
import { ChatInput } from '@components/ui/chat/chat-input';
import { ChatMessageList } from '@components/ui/chat/chat-message-list';
import { useGetAuditMetadataQuery } from '@dieterApi/audit/useAuditMetadataQuery';
import { RefreshCcw, Send, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import dieter_kopf from '/assets/images/dieter_kopf.svg';

interface ChatV2Props {
  applicationId?: string;
  displayMode?: 'full' | 'inputOnly'; // either full chat block or input only with a floating chat log
  disableInput?: boolean;
}

interface SuggestionButton {
  text: string;
}

export function ChatV2({
  applicationId: propApplicationId,
  displayMode = 'full', // default full display
  disableInput = false, // whether to disable input field
}: ChatV2Props = {}) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Hide scrollbar for Chrome, Safari and Opera */
      .no-scrollbar::-webkit-scrollbar {
        display: none;
      }

      /* Hide scrollbar for IE, Edge and Firefox */
      .no-scrollbar {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { user } = useUser();
  const { whitelabelConfig } = useUserWithCompany();
  const { isConnected } = useApolloClient();
  const {
    params: { applicationId: routeApplicationId },
  } = useRouteMatch<{ applicationId?: string }>();

  const avatarImage =
    whitelabelConfig?.avatarB64 && whitelabelConfig.avatarB64 !== 'unset' ? whitelabelConfig.avatarB64 : dieter_kopf;

  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // For inputOnly mode
  const floatingChatRef = useRef<HTMLDivElement>(null); // Ref for floating chat window
  const chatContainerRef = useRef<HTMLDivElement>(null); // Ref for the entire chat container
  const [suggestions, setSuggestions] = useState<SuggestionButton[]>([]);

  // prioritize prop applicationId, then route applicationId, then fallback
  const applicationId = propApplicationId || routeApplicationId || 'app-dse-privacy-policy-create';

  // Get audit metadata for progress updates
  const { refetch: refetchAuditProgress } = useGetAuditMetadataQuery(applicationId);

  let latestChat = user?.auditChats?.reduce(
    (acc, chat) => (chat.createdAt > acc.createdAt ? chat : acc),
    user.auditChats[0]
  );

  if (!latestChat) {
    latestChat = user?.auditChats?.reduce(
      (acc, chat) => (chat.createdAt > acc.createdAt ? chat : acc),
      user?.auditChats[0]
    );
  }

  const chat = useChat(
    {
      chatId: latestChat?.id || '',
      applicationId: applicationId,
      handlers: {
        onMessageAdded: () => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          }
        },
      },
    },
    {
      chatType: 'audit',
    }
  );

  // new chat if none exists
  useEffect(() => {
    if (!latestChat) {
      chat.addChat({
        variables: {
          applicationId: applicationId,
        },
      });
    }
  }, [latestChat, applicationId, chat]);

  const messagesRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSend = (message?: string) => {
    const messageToSend = message || chat.input;
    if (!messageToSend.trim()) return;
    chat.submitMessage(messageToSend);
    setIsWaitingForResponse(true);

    if (!message) {
      // clear the input only if not sending a suggestion
      chat.setInput('');
    }

    // scroll to bottom
    setTimeout(() => {
      if (messagesRef.current) {
        messagesRef.current.scrollTo({
          top: messagesRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (chat.isStreaming || !chat.input.trim()) return;
      handleSend();
    }
  };

  // auto-scroll while streaming and refocus input when done
  useEffect(() => {
    const win = messagesRef.current;
    let interval: any;

    if (win && chat.isStreaming) {
      interval = setInterval(() => {
        win.scrollTo({
          top: win.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    } else if (!chat.isStreaming && interval) {
      clearInterval(interval);

      // Refocus input when streaming is done
      setTimeout(() => {
        const inputElement = formRef.current?.querySelector('textarea');
        if (inputElement && (displayMode === 'full' || isExpanded)) {
          inputElement.focus();
        }
      }, 100);
    }

    return () => {
      clearInterval(interval);
    };
  }, [chat.isStreaming, displayMode, isExpanded]);

  // scroll to bottom when chat expands in inputOnly mode
  useEffect(() => {
    if (isExpanded && displayMode === 'inputOnly' && messagesRef.current) {
      // Delay scroll to allow animation to complete and layout to settle
      const timer = setTimeout(() => {
        if (messagesRef.current) {
          messagesRef.current.scrollTo({
            top: messagesRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 100); // delay a bit, so we scroll after the floating chat has opened fully
      return () => clearTimeout(timer);
    }
  }, [isExpanded, displayMode]);

  const deleteMessage = (index: number) => {
    chat.messages.splice(index, 1);
    chat.setMessages([...chat.messages]);
  };

  const extractSuggestions = (content: string): SuggestionButton[] => {
    const suggestionRegex = /<!-- SUGGESTIONS::(.+?) -->/;
    const match = content.match(suggestionRegex);

    if (match && match[1]) {
      const suggestionTexts = match[1].split('|');
      return suggestionTexts.map((text) => ({ text: text.trim() }));
    }

    return [];
  };

  useEffect(() => {
    if (chat.messages.length > 0) {
      const lastMessage = chat.messages[chat.messages.length - 1];

      if (lastMessage.role === 'assistant' && lastMessage.content.trim() !== '') {
        setIsWaitingForResponse(false);

        const extractedSuggestions = extractSuggestions(lastMessage.content);
        setSuggestions(extractedSuggestions);

        // Refetch audit progress after assistant message
        refetchAuditProgress();

        // Refocus input after response is complete
        setTimeout(() => {
          const inputElement = formRef.current?.querySelector('textarea');
          if (inputElement && (displayMode === 'full' || isExpanded)) {
            inputElement.focus();
          }
        }, 100);
      }

      if (lastMessage.role === 'user') {
        setIsWaitingForResponse(true);
        setSuggestions([]);
      }
    }
  }, [chat.messages, displayMode, isExpanded]);

  const handleInputFocus = () => {
    if (displayMode === 'inputOnly') {
      setIsExpanded(true);
    }
  };

  const handleCloseFloatingChat = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsExpanded(false);
  };

  // Click focus/display-toggle handling for floating chat
  useEffect(() => {
    if (displayMode !== 'inputOnly' || !isExpanded) return;
    function handleClickOutside(event: MouseEvent) {
      const chatContainer = chatContainerRef.current;
      if (chatContainer && !chatContainer.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, displayMode]);

  if (!user) return null;

  const renderChatHeader = () => (
    <header className="border-b border-gray-200 flex items-center justify-between p-4">
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          <Avatar className="h-14 w-14">
            <AvatarImage src={avatarImage} alt="Dieter" />
            <AvatarFallback>🤖</AvatarFallback>
          </Avatar>
        </div>
        <div>
          <div className="text-sm font-medium">Dieter</div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span data-testid="chat-status-indicator">{isConnected ? 'online' : 'offline'}</span>
            <span className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            chat.addChat({
              variables: {
                applicationId: applicationId,
              },
            });
            setSuggestions([]);
          }}
          title="Chat zurücksetzen"
        >
          <RefreshCcw className="h-5 w-5" />
        </Button>
      </div>
      <div className="flex items-center">
        <div className="text-[0.65rem] max-w-[200px] text-gray-500 text-right">
          Bei der Benutzung des Dieter-Chatbots gilt unsere{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Datenschutzerklärung
          </a>
          .
        </div>
        {displayMode === 'inputOnly' && isExpanded && (
          <Button variant="ghost" size="icon" onClick={handleCloseFloatingChat} title="Chat schließen" className="ml-2">
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>
    </header>
  );

  const renderMessageList = () => (
    <div
      ref={messagesRef}
      className={`flex-1 overflow-y-auto p-4 space-y-4 min-h-0 ${
        displayMode === 'full' ? 'max-h-[calc(100vh-250px)]' : ''
      }`}
    >
      <ChatMessageList>
        {chat.messages.map((message, index) => {
          const isEmptyAssistantMessage =
            message.role === 'assistant' && message.content === '' && isWaitingForResponse;
          return (
            <ChatBubble
              key={index}
              variant={message.role === 'user' ? 'sent' : 'received'}
              className={isEmptyAssistantMessage ? 'hidden' : ''}
            >
              {message.role !== 'user' && (
                <div className="hidden 2xl:block">
                  <ChatBubbleAvatar className="sm:block h-12 w-12" src={avatarImage} fallback={'🤖'} />
                </div>
              )}
              <ChatBubbleMessage className={message.role === 'user' ? 'bg-secondary-50 text-black' : 'bg-primary-50'}>
                <Markdown text={message.content || ''} />
              </ChatBubbleMessage>
              <ChatBubbleActionWrapper variant={message.role === 'user' ? 'sent' : 'received'}>
                <ChatBubbleAction
                  variant="outline"
                  className="size-5"
                  icon={<Trash2 className="size-20" />}
                  onClick={() => deleteMessage(index)}
                />
              </ChatBubbleActionWrapper>
            </ChatBubble>
          );
        })}
        {isWaitingForResponse && (
          <ChatBubble variant="received">
            <ChatBubbleAvatar className="hidden sm:block h-12 w-12" src={avatarImage} fallback="🤖" />
            <ChatBubbleMessage className="bg-primary-50">
              <img
                src="/assets/images/pending_message_animation.gif"
                alt="Loading..."
                className="w-32 h-32 object-contain"
                style={{ imageRendering: 'auto' }}
              />
            </ChatBubbleMessage>
          </ChatBubble>
        )}
      </ChatMessageList>
    </div>
  );

  const renderSuggestionButtons = () => {
    if (
      (displayMode === 'inputOnly' && !isExpanded) ||
      !suggestions ||
      suggestions.length === 0 ||
      chat.isStreaming ||
      isWaitingForResponse
    )
      return null;

    return (
      <div className="flex gap-2 px-2 overflow-x-auto overflow-y-hidden pb-1 no-scrollbar whitespace-nowrap">
        {suggestions.slice(0, 3).map((suggestion, index) => (
          <Badge
            key={`suggestion-${suggestion.text}`}
            variant="outline"
            className="cursor-pointer hover:bg-secondary-50 transition-colors py-1 px-3 text-sm rounded-lg"
            style={{
              animation: 'fadeInUp 300ms ease forwards',
              animationDelay: `${index * 100}ms`,
              opacity: 0,
            }}
            onClick={() => {
              handleSend(suggestion.text);
              setTimeout(() => {
                // focus back on input field
                const inputElement = formRef.current?.querySelector('textarea');
                if (inputElement) {
                  inputElement.focus();
                }
              }, 0);
            }}
          >
            {suggestion.text}
          </Badge>
        ))}
      </div>
    );
  };

  const renderChatInputForm = () => (
    <div className="bg-white rounded-lg shadow-sm border p-2">
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="relative flex flex-col"
      >
        <div className="h-8 overflow-x-auto overflow-y-hidden">{renderSuggestionButtons()}</div>
        <div className="flex items-center">
          <ChatInput
            disabled={disableInput || chat.isStreaming || isWaitingForResponse}
            value={chat.input}
            onKeyDown={onKeyDown}
            onChange={(e) => {
              chat.handleInputChange(e);
            }}
            onFocus={handleInputFocus}
            placeholder={
              chat.isStreaming || isWaitingForResponse
                ? 'Dieter antwortet...'
                : 'Gib hier ein, was du brauchst oder wissen willst...'
            }
            className="flex-1 border-0 py-2 px-3 pr-10 focus:ring-0 focus-visible:ring-0 resize-none placeholder:text-base"
            rows={1}
          />
          <Button
            type="submit"
            size="icon"
            className="absolute right-2"
            disabled={!chat.input.trim() || chat.isStreaming || isWaitingForResponse}
            title="Senden"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );

  if (displayMode === 'inputOnly') {
    return (
      <div ref={chatContainerRef} className="relative w-full">
        <div className="rounded-lg border bg-white shadow-sm">{renderChatInputForm()}</div>
        {/* floating chat window, visually and mouse-event transparent when not shown */}
        <div
          ref={floatingChatRef}
          className={`absolute bottom-[calc(100%+8px)] left-0 w-full z-50 max-h-[80vh] min-h-[80vh] flex flex-col bg-white rounded-lg border shadow-sm overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          {renderChatHeader()}
          {renderMessageList()}
        </div>
      </div>
    );
  }

  // full display mode
  return (
    <div className="flex flex-col w-full h-full gap-4 max-h-[calc(100vh)]">
      <div className="rounded-lg border bg-white shadow-sm flex flex-col grow overflow-hidden">
        {renderChatHeader()}
        {renderMessageList()}
      </div>
      <div className="rounded-lg border bg-white shadow-sm mt-auto">{renderChatInputForm()}</div>
    </div>
  );
}
